import React, { useMemo, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Legend,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useNavigate } from "react-router-dom";
import {
  endOfDay,
  endOfMonth,
  isValid,
  parse,
  startOfDay,
  startOfMonth,
} from "date-fns";
import { colors } from "./colors";
import { getBadgeColor, StatusBadge } from "../../components/StatusBadge";

const badgeColorToLegacyPalette = {
  // Keep the original dashboard chart vibe (soft palette),
  // while aligning to the actual status color intent.
  primary: colors.primary,
  gray: "#9ca3af",
  slate: "#94a3b8",
  zinc: "#a1a1aa",
  neutral: "#a3a3a3",
  stone: "#a8a29e",
  orange: colors.orange,
  yellow: "#facc15",
  amber: "#fbbf24",
  red: "#fb7185",
  rose: "#fb7185",
  pink: "#f472b6",
  blue: colors.blue,
  sky: "#7dd3fc",
  indigo: "#818cf8",
  cyan: colors.cyan,
  teal: "#5eead4",
  purple: colors.purple,
  fuchsia: "#e879f9",
  green: colors.green,
  emerald: "#6ee7b7",
  lime: "#a3e635",
};

const getStatusHex = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();
  if (normalizedStatus === "dispatched") {
    // Keep in the same soft palette family but shift dispatched toward yellow.
    return "#facc15";
  }
  const badgeColor = getBadgeColor(status, "job");
  return badgeColorToLegacyPalette[badgeColor] || colors.primary;
};

const getGradientId = (key) =>
  `status-grad-${String(key)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")}`;

/**
 * Dynamic Area Chart component that handles different types of status data visualization
 * @param {Object} props - Component props
 * @param {Array} props.data - Chart data array
 * @param {string} props.title - Chart title
 * @param {string} props.appType - Application type (serviceRepair/trucking)
 * @param {string} props.dataType - Data type to display (jobs/invoices)
 * @param {number} props.height - Chart height
 */
export const AreaChartComponent = ({
  data,
  title,
  appType,
  dataType = "jobs",
  viewType,
  rangeStart,
  rangeEnd,
  ...props
}) => {
  const navigate = useNavigate();
  const [isTooltipPinned, setIsTooltipPinned] = useState(false);
  const [pinnedTooltipState, setPinnedTooltipState] = useState({
    active: false,
    payload: [],
    label: "",
    coordinate: null,
  });

  const getBucketRange = (bucketName) => {
    if (typeof bucketName !== "string") return null;
    const trimmed = bucketName.trim();

    // Monthly/yearly bucket label (MM-YYYY)
    if (/^\d{2}-\d{4}$/.test(trimmed)) {
      const date = parse(trimmed, "MM-yyyy", new Date());
      if (isValid(date)) {
        return { start: startOfMonth(date), end: endOfMonth(date) };
      }
      return null;
    }

    // Day bucket label (MM-DD-YYYY)
    if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
      const date = parse(trimmed, "MM-dd-yyyy", new Date());
      if (isValid(date)) {
        return { start: startOfDay(date), end: endOfDay(date) };
      }
      return null;
    }

    // Legacy day format (MM/DD/YYYY)
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
      const date = parse(trimmed, "MM/dd/yyyy", new Date());
      if (isValid(date)) {
        return { start: startOfDay(date), end: endOfDay(date) };
      }
      return null;
    }

    return null;
  };

  const getStatusFilterLink = (status, pointData) => {
    const bucketRange = getBucketRange(pointData?.Name);
    const fallbackStart = rangeStart ? new Date(rangeStart) : null;
    const fallbackEnd = rangeEnd ? new Date(rangeEnd) : null;

    const start = bucketRange?.start || (isValid(fallbackStart) ? fallbackStart : null);
    const end = bucketRange?.end || (isValid(fallbackEnd) ? fallbackEnd : null);

    const params = new URLSearchParams({
      source: "dashboard",
      status,
    });
    if (start && end) {
      params.set("startDate", start.toISOString());
      params.set("endDate", end.toISOString());
    }
    return `/jobs?${params.toString()}`;
  };

  const getDateFilterLink = (pointData) => {
    const bucketRange = getBucketRange(pointData?.Name);
    const fallbackStart = rangeStart ? new Date(rangeStart) : null;
    const fallbackEnd = rangeEnd ? new Date(rangeEnd) : null;

    const start = bucketRange?.start || (isValid(fallbackStart) ? fallbackStart : null);
    const end = bucketRange?.end || (isValid(fallbackEnd) ? fallbackEnd : null);

    const params = new URLSearchParams({ source: "dashboard" });
    if (start && end) {
      params.set("startDate", start.toISOString());
      params.set("endDate", end.toISOString());
    }
    return `/jobs?${params.toString()}`;
  };

  const renderLegendContent = ({ payload = [] }) => {
    if (dataType !== "jobs") {
      return (
        <div className="flex w-full flex-wrap items-center justify-center gap-3 pt-1 text-xs text-gray-600 text-center">
          {payload.map((entry) => (
            <span key={entry.value} className="inline-flex items-center gap-1">
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              {entry.value}
            </span>
          ))}
        </div>
      );
    }

    return (
      <div className="flex w-full flex-wrap items-center justify-center gap-2 pt-1 text-center">
        {payload.map((entry) => (
          <StatusBadge
            key={entry.value}
            status={entry.value}
            component="job"
            className="inline-flex"
          />
        ))}
      </div>
    );
  };

  // Define status configurations based on appType and dataType
  const statusConfig = useMemo(
    () => ({
      jobs: {
        serviceRepair: [
          { key: "New" },
          { key: "Pending" },
          { key: "Booked" },
          { key: "Assigned" },
          { key: "In Progress" },
          { key: "Completed" },
          { key: "Invoiced" },
        ],
        trucking: [
          { key: "New" },
          { key: "Scheduled" },
          { key: "Dispatched" },
          { key: "In Progress" },
          { key: "Completed" },
          { key: "Invoiced" },
          { key: "Paid" },
        ],
      },
      invoices: {
        all: [
          { key: "Review", color: colors.primary, gradientId: "five" },
          { key: "Sent", color: colors.orange, gradientId: "six" },
          { key: "Paid", color: colors.green, gradientId: "one" },
          { key: "Total", color: colors.blue, gradientId: "four" },
        ],
      },
    }),
    []
  );

  // Get active statuses based on dataType and appType
  const activeStatuses = useMemo(() => {
    if (dataType === "invoices") {
      return statusConfig.invoices.all.map((status) => ({
        ...status,
        gradientId: getGradientId(status.key),
      }));
    }
    const statuses =
      appType === "serviceRepair"
      ? statusConfig.jobs.serviceRepair
      : statusConfig.jobs.trucking;
    return statuses.map((status) => ({
      ...status,
      color: getStatusHex(status.key),
      gradientId: getGradientId(status.key),
    }));
  }, [appType, dataType, statusConfig]);

  // Define currency fields at component level or via props
  const currencyFields = ["OutstandingAmount", "PaidAmount", "TotalAmount"]; // adjust based on your needs

  // Create a reusable currency formatter
  const formatCurrency = (value) => {
    // Ensure value is a number and handle potential null/undefined
    const numValue = Number(value);
    if (isNaN(numValue)) return "$0.00";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(numValue);
  };

  const CustomTooltip = ({ active, payload }) => {
    const resolvedActive = isTooltipPinned ? pinnedTooltipState.active : active;
    const resolvedPayload = isTooltipPinned ? pinnedTooltipState.payload : payload;

    if (resolvedActive && resolvedPayload && resolvedPayload.length) {
      const data = resolvedPayload[0].payload;

      // Get the right statuses to display based on dataType and appType
      let statusesToShow = [];
      if (dataType === "invoices") {
        statusesToShow = [
          { key: "Review", format: "number" },
          { key: "Sent", format: "number" },
          { key: "Paid", format: "number" },
          { key: "Total", format: "number" },
          { key: "OutstandingAmount", format: "currency" },
          { key: "PaidAmount", format: "currency" },
          { key: "TotalAmount", format: "currency" },
        ];
      } else if (appType === "serviceRepair") {
        statusesToShow = [
          { key: "New", format: "number" },
          { key: "Pending", format: "number" },
          { key: "Booked", format: "number" },
          { key: "Assigned", format: "number" },
          { key: "In Progress", format: "number" },
          { key: "Completed", format: "number" },
          { key: "Invoiced", format: "number" },
          { key: "Total", format: "number" },
        ];
      } else {
        // Trucking
        statusesToShow = [
          { key: "New", format: "number" },
          { key: "Scheduled", format: "number" },
          { key: "Dispatched", format: "number" },
          { key: "In Progress", format: "number" },
          { key: "Completed", format: "number" },
          { key: "Invoiced", format: "number" },
          { key: "Paid", format: "number" },
          { key: "Total", format: "number" },
        ];
      }

      return (
        <div className="custom-tooltip bg-white p-3 border border-gray-200 rounded shadow">
          <div className="flex items-center justify-between border-b pb-2 mb-2">
            {dataType === "jobs" ? (
              <button
                type="button"
                className="font-bold text-lg text-left hover:underline"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(getDateFilterLink(data));
                }}
              >
                {data.Name}
              </button>
            ) : (
              <p className="font-bold text-lg">{data.Name}</p>
            )}
            {isTooltipPinned ? (
              <button
                type="button"
                className="text-xs text-primary-600 hover:underline"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsTooltipPinned(false);
                }}
              >
                Unpin
              </button>
            ) : null}
          </div>
          <div className="space-y-1">
            {statusesToShow.map(({ key, format }) => {
              if (data[key] !== undefined) {
                const isJobStatusRow =
                  dataType === "jobs" && key !== "Total" && Number(data[key]) > 0;
                return (
                  <div key={key} className="flex justify-between">
                    <span className="text-gray-600 inline-flex items-center">
                      {isJobStatusRow ? (
                        <button
                          type="button"
                          className="mr-2"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(getStatusFilterLink(key, data));
                          }}
                        >
                          <StatusBadge status={key} component="job" />
                        </button>
                      ) : dataType === "jobs" && key !== "Total" ? (
                        <StatusBadge status={key} component="job" className="mr-2" />
                      ) : (
                        <span>{key.replace(/([A-Z])/g, " $1").trim()}:</span>
                      )}
                    </span>
                    <span className="font-medium ml-4">
                      {format === "currency"
                        ? formatCurrency(data[key])
                        : new Intl.NumberFormat("en-US", {
                            maximumFractionDigits: 0,
                          }).format(data[key])}
                    </span>
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={props.height}>
      <AreaChart
        data={data}
        style={{ cursor: isTooltipPinned ? "default" : "pointer" }}
        onMouseMove={(state) => {
          if (isTooltipPinned) return;
          if (state?.activePayload?.length) {
            setPinnedTooltipState({
              active: true,
              payload: state.activePayload,
              label: state.activeLabel || "",
              coordinate: state.activeCoordinate
                ? { ...state.activeCoordinate }
                : null,
            });
          }
        }}
        onMouseLeave={() => {
          if (isTooltipPinned) return;
          setPinnedTooltipState({
            active: false,
            payload: [],
            label: "",
            coordinate: null,
          });
        }}
        onClick={(state) => {
          if (state?.activePayload?.length) {
            setPinnedTooltipState({
              active: true,
              payload: state.activePayload,
              label: state.activeLabel || "",
              coordinate: state.activeCoordinate
                ? { ...state.activeCoordinate }
                : null,
            });
            setIsTooltipPinned(true);
            return;
          }
          setIsTooltipPinned(false);
        }}
        margin={{
          top: 10,
          right: 10,
          left: 0,
          bottom: 0,
        }}
      >
        <CartesianGrid opacity={0.5} vertical={false} />
        <XAxis dataKey="Name" tick={{ fontSize: 13 }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 13 }} />

        <defs>
          {activeStatuses.map(({ key, color, gradientId }) => (
            <linearGradient key={key} id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.8} />
              <stop offset="95%" stopColor={color} stopOpacity={0.2} />
            </linearGradient>
          ))}
        </defs>
        <Tooltip
          content={<CustomTooltip />}
          wrapperStyle={{ pointerEvents: "auto" }}
          active={isTooltipPinned ? pinnedTooltipState.active : undefined}
          payload={isTooltipPinned ? pinnedTooltipState.payload : undefined}
          label={isTooltipPinned ? pinnedTooltipState.label : undefined}
          position={isTooltipPinned ? pinnedTooltipState.coordinate : undefined}
        />
        <Legend
          align="center"
          wrapperStyle={{ fontSize: "13px", width: "100%", left: 0 }}
          content={renderLegendContent}
        />

        {activeStatuses.map(({ key, color, gradientId }) => (
          <Area
            key={key}
            type="monotone"
            strokeWidth={3}
            dataKey={key}
            fillOpacity={1}
            stroke={color}
            fill={`url(#${gradientId})`}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default AreaChartComponent;
